import { useCallback, useEffect, useRef, useState } from "react";

import { ApiError, createRun, getRun, listRuns, listUnitRuns } from "@/api/client";
import type { Run, RunMode, UnitRun } from "@/api/client";

/** A run in one of these states is still spending; the panel polls it. */
export const ACTIVE: ReadonlySet<string> = new Set(["queued", "running"]);
const POLL_MS = 2000;

export interface Runs {
  /** The newest run of the document, or null before any has been started. */
  run: Run | null;
  /** True while `run` is queued or running. */
  active: boolean;
  /** Per-unit progress of `run`, refreshed with every poll; empty before the link stage. */
  unitRuns: UnitRun[];
  /** Increments each time a run reaches a terminal state, so pages can refetch. */
  finishedCount: number;
  error: string | null;
  /** Starts a run; `mode` says how units are found (the API decides when `auto`). */
  start: (mode?: RunMode) => Promise<void>;
  starting: boolean;
  startError: string | null;
}

/**
 * The latest take-off run of a document. Loads it once, polls every two seconds while
 * it is queued or running, and stops on a terminal status. `start` posts a new run and
 * hands it to the same poller.
 */
export function useRuns(documentId: string): Runs {
  const [run, setRun] = useState<Run | null>(null);
  const [unitRuns, setUnitRuns] = useState<UnitRun[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [finishedCount, setFinishedCount] = useState(0);
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);
  const wasActive = useRef(false);

  useEffect(() => {
    let cancelled = false;
    listRuns(documentId)
      .then((all) => {
        if (!cancelled) {
          setRun(all[0] ?? null);
          setError(null);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.detail : "could not load runs");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [documentId]);

  const runId = run?.id;
  const active = run !== null && ACTIVE.has(run.status);

  // Unit runs load once per run and again on every poll while it is active; a failure
  // here is not worth an error banner, the run's own counters still show.
  const unitsTotal = run?.units_total ?? 0;
  useEffect(() => {
    if (!runId) {
      setUnitRuns([]);
      return;
    }
    let cancelled = false;
    listUnitRuns(runId)
      .then((rows) => {
        if (!cancelled) {
          setUnitRuns(rows);
        }
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [runId, unitsTotal, active]);

  // Count the transition active -> terminal exactly once per run.
  useEffect(() => {
    if (active) {
      wasActive.current = true;
    } else if (wasActive.current) {
      wasActive.current = false;
      setFinishedCount((n) => n + 1);
    }
  }, [active, runId]);

  useEffect(() => {
    if (!runId || !active) {
      return;
    }
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const tick = async () => {
      try {
        const next = await getRun(runId);
        if (cancelled) {
          return;
        }
        setRun(next);
        setError(null);
        if (next.units_total > 0) {
          listUnitRuns(runId)
            .then((rows) => {
              if (!cancelled) {
                setUnitRuns(rows);
              }
            })
            .catch(() => undefined);
        }
        if (ACTIVE.has(next.status)) {
          timer = setTimeout(() => void tick(), POLL_MS);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.detail : "could not reach the API");
          timer = setTimeout(() => void tick(), POLL_MS);
        }
      }
    };
    timer = setTimeout(() => void tick(), POLL_MS);
    return () => {
      cancelled = true;
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [runId, active]);

  const start = useCallback(
    async (mode?: RunMode) => {
      setStarting(true);
      setStartError(null);
      try {
        const created = await createRun(documentId, mode ? { mode } : {});
        setRun(created);
      } catch (err) {
        setStartError(err instanceof ApiError ? err.detail : "could not start the run");
      } finally {
        setStarting(false);
      }
    },
    [documentId],
  );

  return { run, active, unitRuns, finishedCount, error, start, starting, startError };
}
